import { memo } from 'react';
import { TestId } from '@/types/testId';
import { getByPath } from '@/utils/widgets';
import { CodeViewer } from '@/components/ui/utils/code/codeViewer';
import type { CodeView } from './code.type';
import type { WidgetData } from '@/components/ui/widget/widget.type';

type Props = {
  view: CodeView;
  data: WidgetData;
};

// display a code snippet in codemirror
const CodeViewImpl = memo(({ view, data }: Props) => {
  const { path, codemirrorOptions } = view;

  let codeData = getByPath(data, path);
  if (codeData === undefined) return null;

  codeData = JSON.stringify(codeData, null, 2);

  return (
    <div className="overflow-hidden" data-testid={TestId.Widget.Code.CodeView}>
      <CodeViewer codeString={codeData} codemirrorOptions={codemirrorOptions} />
    </div>
  );
});

// sort defaults to text on path
const getSortConfig = (view: CodeView) => ({
  type: 'text',
  path: view.path
});

// filtering defaults to text search on path
const getFilterConfig = (view: CodeView) => ({
  type: 'text',
  path: view.path
});

// width defaults to 'xlarge'
const getDefaultWidth = () => 'xlarge';

// stringifies data at path
const getStringifyFunction = (view: CodeView) => {
  const { path } = view;

  return (data: WidgetData) => {
    const codeData = getByPath(data, path);
    if (codeData === undefined) return '';

    return JSON.stringify(codeData, null, 2);
  };
};

export const codeViewProvider = {
  component: CodeViewImpl,
  getSortConfig,
  getFilterConfig,
  getDefaultWidth,
  getStringifyFunction
};
